import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DragHandleIcon from "@mui/icons-material/DragIndicator";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    Autocomplete,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    TextareaAutosize,
    Typography,
    createFilterOptions,
    styled,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { FileUploader } from "react-drag-drop-files";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import {
    ArrayHelpers,
    Field,
    FieldArray,
    Form,
    Formik,
    FormikValues,
} from "formik";
import * as yup from "yup";
import {
    BANNER_PLACEHOLDER_MESSAGE_WEB,
    BANNER_TYPES,
    BANNER_TYPES_CATEGORY_1,
    BANNER_TYPES_CATEGORY_2,
    SYNOPSIS_LIMIT,
    TITLE_TEXT_LIMIT,
    BANNER_ERROR_MESSAGE,
    BANNER_DIMENSION,
    BANNER_ACCEPTED_FILE,
    GENRE_RAIL_TITLE,
} from "../LandingPage/constants";
import {
    DeleteForeverOutlined,
    DeleteForeverRounded,
} from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import { ids } from "webpack";
import { validateImageURL } from "../../../utilities/helpers/helperfunctions";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { StyledDropzone } from "../../UI/Common/FileUploader";
import axios from "../../../utils/axiosInstance";
import { useSnackbar } from "notistack";
import GeneralInfoModal from "../../UI/atoms/Popup/GeneralInfoModal";
import GeneralModalV1 from "../../UI/atoms/Popup/GeneralModalV1";

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
}));

const fileTypes = ["JPEG", "PNG", "jpeg", "jpg", "png"];

const GenreForm = forwardRef(
    (
        {
            genreMasterList,
            handleGenres,
            genres,
            draft,
            videoAPILoader,
            allowEdit,
        }: any,
        ref: any
    ) => {
        const [genreList, setGenreList] = useState<any>([]);
        const [isOpen, setIsOpen] = React.useState<any>(null);
        const formikRef = useRef<any>(null);
        const [initialValue, setInitialValue] = useState<any>([]);
        const [formSubmitInitated, setFormSubmitInitiated] = useState(false);
        const [formChange, setFormChange] = useState<any>(null);

        const initialValuesState = {
            genreId: null,
            genreTitle: null,
            tags: null,
        };

        // Set Genre List in the state
        useEffect(() => {
            setGenreList(genreMasterList);
        }, [genreMasterList]);

        // To handle new/draft/no data for genre rail
        useEffect(() => {
            if (draft) {
                setInitialValue(draft);
            } else if (genres) {
                setInitialValue(genres);
            } else {
                setInitialValue([]);
            }
        }, [genres]);

        // Autocomplete open & close
        const handleOpenOptions = (index: number) => {
            setIsOpen(index);
        };

        const handleCloseOptions = (index: number) => {
            setIsOpen(null);
        };

        // formik validation
        const dynamicValidationSchema = yup.object().shape({
            genres: yup.array().of(
                yup.object().shape({
                    genreId: yup.string().required(),
                    genreTitle: yup.string().required(),
                    tags: yup.string().required(),
                })
            ),
        });

        const handleOnSubmit = async (values: FormikValues) => {
            handleGenres(values);
        };

        // Autocomplete rendering options labels
        const renderOptions = (options: any) => {
            if (typeof options == "string") {
                const genreListInfo = genreList.filter(
                    (list: any) => list.genreId === options
                );
                if (genreListInfo.length > 0) {
                    return genreListInfo[0].genreTitle;
                } else {
                    return "";
                }
            } else if (typeof options == "object") {
                return options.genreTitle;
            }
        };

        const handleSubmitFormik = () => {
            console.log("formikRef.current", formikRef.current);
            formikRef.current.submitForm();
            setFormSubmitInitiated(false);
        };

        const getFormValues = () => {
            console.log("formikRef.current", formikRef.current);
            return formikRef.current.values;
        };

        const isValidForm = () => {
            console.log("formikRef.current", formikRef.current);
            const isValid = formikRef.current.isValid;
            if (!isValid) setFormSubmitInitiated(true);
            return isValid;
        };

        useImperativeHandle(
            ref,
            () => ({
                handleSubmitFormik,
                getFormValues,
                isValidForm,
                validateForm: () => formikRef.current.validateForm(),
            }),
            [formChange]
        );

        const grid = 4;

        const getItemStyle = (isDragging: any, draggableStyle: any) => ({
            // some basic styles to make the items look a bit nicer
            userSelect: "none",
            padding: grid * 2,
            margin: 0,
            marginTop: 10,
            backgound: "white !important",
            ...draggableStyle,
        });

        const getListStyle = (isDraggingOver: any) => ({
            padding: 10,
            width: "100%",
            overflow: "hidden",
        });

        const onDragEnd = (result: any) => {
            try {
                let currentValues: any[] = [];
                if (!result.destination) {
                    return;
                }
                if (
                    result?.destination?.droppableId ===
                        result?.source?.droppableId &&
                    result?.destination?.index === result?.source?.index
                ) {
                    return;
                }
                currentValues = [...formikRef?.current?.values?.genres];
                const reordered = reorder(
                    currentValues,
                    result?.source?.index,
                    result?.destination?.index
                );
                formikRef.current.setValues({ genres: reordered });
            } catch (error) {
                console.log("Error when drag end", error);
            }
        };

        const reorder = (list: any, startIndex: any, endIndex: any) => {
            const result = Array.from(list);
            const [removed] = result.splice(startIndex, 1);
            result.splice(endIndex, 0, removed);
            return result;
        };

        return (
            <div className="d-flex float-center flex-column m-2">
                <Formik
                    innerRef={formikRef}
                    initialValues={{
                        genres: initialValue,
                    }}
                    enableReinitialize
                    validationSchema={dynamicValidationSchema}
                    onSubmit={handleOnSubmit}
                >
                    {({
                        values,
                        handleChange,
                        touched,
                        errors,
                        setFieldValue,
                        setFieldTouched,
                    }: any) => (
                        <Form className="config-form">
                            <FieldArray
                                name="genres"
                                render={(arrayHelpers: ArrayHelpers) => {
                                    const genres = values.genres;
                                    return (
                                        <div>
                                            {genres && genres.length == 0 && (
                                                <Grid>
                                                    <Grid item xs={12}>
                                                        <div className="d-flex flex-column j-center align-item-center">
                                                            <AddCircleOutlineIcon
                                                                onClick={() => {
                                                                    arrayHelpers.push(
                                                                        {
                                                                            ...initialValuesState,
                                                                        }
                                                                    );
                                                                }}
                                                                className="cursor-pointer"
                                                            />
                                                            <span>
                                                                Add Genre
                                                            </span>
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                            )}
                                            <DragDropContext
                                                onDragEnd={onDragEnd}
                                            >
                                                <Droppable
                                                    droppableId="droppable-child-curation"
                                                    type={`child`}
                                                    key={`droppable-genre-container`}
                                                >
                                                    {(provided, snapshot) => (
                                                        <div
                                                            className="d-flex j-center mt-1 flex-column"
                                                            {...provided.droppableProps}
                                                            ref={
                                                                provided.innerRef
                                                            }
                                                            style={getListStyle(
                                                                snapshot.isDraggingOver
                                                            )}
                                                        >
                                                            {genres &&
                                                                genres.length >
                                                                    0 &&
                                                                genres.map(
                                                                    (
                                                                        item: any,
                                                                        index: number
                                                                    ) => (
                                                                        <Draggable
                                                                            isDragDisabled={
                                                                                !allowEdit
                                                                            }
                                                                            key={
                                                                                index
                                                                            }
                                                                            draggableId={`genres-${index}`}
                                                                            index={
                                                                                index
                                                                            }
                                                                        >
                                                                            {(
                                                                                provided,
                                                                                snapshot
                                                                            ) => (
                                                                                <Grid
                                                                                    ref={
                                                                                        provided.innerRef
                                                                                    }
                                                                                    {...provided.draggableProps}
                                                                                    style={getItemStyle(
                                                                                        snapshot.isDragging,
                                                                                        provided
                                                                                            .draggableProps
                                                                                            .style
                                                                                    )}
                                                                                    key={
                                                                                        index
                                                                                    }
                                                                                    className="box-shadow-s mb-3 bg-white"
                                                                                    container
                                                                                    spacing={
                                                                                        1
                                                                                    }
                                                                                >
                                                                                    <Grid
                                                                                        {...provided.dragHandleProps}
                                                                                        className="d-flex align-item-center"
                                                                                        item
                                                                                    >
                                                                                        <DragHandleIcon className="drag-handle" />
                                                                                        <span
                                                                                            style={{
                                                                                                fontSize:
                                                                                                    "1rem",
                                                                                                fontWeight:
                                                                                                    "bold",
                                                                                            }}
                                                                                        >
                                                                                            #
                                                                                            {index +
                                                                                                1}
                                                                                        </span>
                                                                                    </Grid>
                                                                                    {/* Banner dropdown - with Drag handle starts */}
                                                                                    <Grid
                                                                                        className="d-flex"
                                                                                        item
                                                                                        xs={
                                                                                            10
                                                                                        }
                                                                                    >
                                                                                        <div className="w-100">
                                                                                            <div className="d-flex">
                                                                                                <Autocomplete
                                                                                                    id={
                                                                                                        `genreId` +
                                                                                                        index
                                                                                                    }
                                                                                                    options={
                                                                                                        genreMasterList
                                                                                                    }
                                                                                                    isOptionEqualToValue={(
                                                                                                        option,
                                                                                                        value
                                                                                                    ) => {
                                                                                                        console.log(
                                                                                                            option,
                                                                                                            "--value",
                                                                                                            value
                                                                                                        );
                                                                                                        return (
                                                                                                            option.genreId ===
                                                                                                            value
                                                                                                        );
                                                                                                    }}
                                                                                                    value={
                                                                                                        values[
                                                                                                            `genres`
                                                                                                        ][
                                                                                                            index
                                                                                                        ][
                                                                                                            `genreId`
                                                                                                        ]
                                                                                                    }
                                                                                                    includeInputInList
                                                                                                    open={
                                                                                                        isOpen ==
                                                                                                        index
                                                                                                    }
                                                                                                    size={
                                                                                                        "small"
                                                                                                    }
                                                                                                    className="mb-1 w-95"
                                                                                                    onChange={(
                                                                                                        e,
                                                                                                        value
                                                                                                    ) => {
                                                                                                        setFormChange(
                                                                                                            {
                                                                                                                genreId:
                                                                                                                    value?.genreId ||
                                                                                                                    "",
                                                                                                                genreTitle:
                                                                                                                    value?.genreTitle ||
                                                                                                                    "",
                                                                                                            }
                                                                                                        );
                                                                                                        setFieldValue(
                                                                                                            `genres.${index}.genreId`,
                                                                                                            value?.genreId ||
                                                                                                                ""
                                                                                                        );
                                                                                                        setFieldValue(
                                                                                                            `genres.${index}.genreTitle`,
                                                                                                            value?.genreTitle ||
                                                                                                                ""
                                                                                                        );
                                                                                                        setFieldValue(
                                                                                                            `genres.${index}.tags`,
                                                                                                            value?.tags ||
                                                                                                                ""
                                                                                                        );
                                                                                                    }}
                                                                                                    onOpen={() =>
                                                                                                        handleOpenOptions(
                                                                                                            index
                                                                                                        )
                                                                                                    }
                                                                                                    onClose={() =>
                                                                                                        handleCloseOptions(
                                                                                                            index
                                                                                                        )
                                                                                                    }
                                                                                                    getOptionLabel={(
                                                                                                        options: any
                                                                                                    ) => {
                                                                                                        return (
                                                                                                            renderOptions(
                                                                                                                options
                                                                                                            ) ||
                                                                                                            ""
                                                                                                        );
                                                                                                    }}
                                                                                                    renderInput={(
                                                                                                        params
                                                                                                    ) => (
                                                                                                        <TextField
                                                                                                            name={`genres.${index}.genreId`}
                                                                                                            label="Genre"
                                                                                                            error={
                                                                                                                errors[
                                                                                                                    `genres`
                                                                                                                ] &&
                                                                                                                errors[
                                                                                                                    `genres`
                                                                                                                ][
                                                                                                                    index
                                                                                                                ]
                                                                                                                    ?.genreId
                                                                                                            }
                                                                                                            {...params}
                                                                                                        />
                                                                                                    )}
                                                                                                    disabled={
                                                                                                        !allowEdit
                                                                                                    }
                                                                                                />
                                                                                            </div>
                                                                                        </div>
                                                                                    </Grid>
                                                                                    {/* Banner dropdown - with Drag handle ends */}

                                                                                    {/* Action buttons for Add/Delete Starts */}
                                                                                    <Grid
                                                                                        className="d-flex j-center align-item-center"
                                                                                        item
                                                                                        xs={
                                                                                            1
                                                                                        }
                                                                                    >
                                                                                        {allowEdit && (
                                                                                            <>
                                                                                                <span className="cursor-pointer">
                                                                                                    <AddCircleOutlineIcon
                                                                                                        className="cursor-pointer"
                                                                                                        onClick={() => {
                                                                                                            arrayHelpers.insert(
                                                                                                                index +
                                                                                                                    1,
                                                                                                                {
                                                                                                                    ...initialValuesState,
                                                                                                                }
                                                                                                            );
                                                                                                        }}
                                                                                                    />
                                                                                                </span>

                                                                                                <span className="delete-circle-style">
                                                                                                    <DeleteForeverRounded
                                                                                                        className="cursor-pointer"
                                                                                                        onClick={() => {
                                                                                                            arrayHelpers.remove(
                                                                                                                index
                                                                                                            );
                                                                                                        }}
                                                                                                    />
                                                                                                </span>
                                                                                            </>
                                                                                        )}
                                                                                    </Grid>
                                                                                    {/* Action buttons for Add/Delete Ends */}
                                                                                </Grid>
                                                                            )}
                                                                        </Draggable>
                                                                    )
                                                                )}
                                                        </div>
                                                    )}
                                                </Droppable>
                                            </DragDropContext>
                                        </div>
                                    );
                                }}
                            />
                        </Form>
                    )}
                </Formik>
            </div>
        );
    }
);

export default GenreForm;
